import { ActiveAuth } from '@tock/logging';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import {
  EventAction,
  EventCustomAttributes,
  EventTimedAction,
  UserProperty,
  UserPropertyDeviceApp,
} from 'utils/analytics/amplitudeEventConstants';
import {
  Identify,
  getDeviceId as amplitudeGetDeviceId,
  identify as amplitudeIdentify,
  init,
  setOptOut,
  setUserId,
  track,
} from '@amplitude/analytics-browser';
import { RoleType, core } from '@tock/coretypes';

/** Used to set up Amplitude in order to track events made in Dashboard. Called from `<Amplitude />`’s `useMountEffect()`. */
export const setupAmplitudeTracking = (key: string | undefined, onInitComplete: () => void) => {
  if (!key) {
    return;
  }

  init(key, {
    defaultTracking: { formInteractions: false, pageViews: false },
    trackingOptions: {
      ipAddress: false,
    },
    appVersion: getBuildNumberSemantic(window.__BUILD_NUMBER__),
  }).promise.then(onInitComplete);

  const appType = getAppType();
  amplitudeSetUserProperty({ property: 'Device app type', value: appType });
};

/**
 * Parses a build number string (legacy or new format) into a semantic version string for Amplitude.
 * - Legacy: YYYY-MM-DDRCXX
 * - New: servingstack-YYYY-MM-DDRCXX
 * Returns '1.0.yyyymmddXX' or undefined if parsing fails.
 * @param buildNumber Optional build number string. If not provided, reads from window.__BUILD_NUMBER__.
 */
export function getBuildNumberSemantic(buildNumber: string | undefined): string | undefined {
  let input = buildNumber;
  if (!input) {
    return undefined;
  }

  // Remove known prefix if present
  if (input.startsWith('servingstack-')) {
    input = input.replace('servingstack-', '');
  }

  // Match:
  // 1. YYYY-MM-DDRCXX (legacy)
  // 2. YYYY-MM-DDRCXX-XX (new, trailing -XX is appended to RC)
  const match = input.match(/^(\d{4}-\d{2}-\d{2})RC(\d{2})(?:-(\d{2}))?$/);
  if (!match) {
    return undefined;
  }

  const [_, dateStr, rcMain, rcExtra] = match;
  const rcStr = rcExtra ? `${rcMain}${rcExtra}` : rcMain;
  try {
    const date = LocalDate.parse(dateStr);
    const formattedDate = date.format(DateTimeFormatter.ofPattern('yyyyMMdd'));
    // amplitude only accepts semantic versioning, so hacking our build number into a semantic-y format
    return `1.0.${formattedDate}${rcStr}`;
  } catch {
    return undefined;
  }
}

export const setDimensions = ({
  businessId,
  businessName,
  businessGroupId,
  businessTypeString,
  businessProductType,
  businessProductLevel,
  roleType,
}: ActiveAuth) => {
  const identify = new Identify();
  identify.set('businessId', businessId);
  identify.set('businessName', businessName);
  identify.set('businessGroupId', businessGroupId);
  identify.set('businessTypeString', businessTypeString);
  identify.set('roleType', RoleType[roleType]);
  if (businessProductType) {
    identify.set('businessProductType', businessProductType);
  }
  if (businessProductLevel) {
    identify.set('businessProductLevel', businessProductLevel);
  }
  amplitudeIdentify(identify);
};

export const allowAmplitudeTracking = () => {
  setOptOut(false);
};

export const revokeAmplitudeTracking = () => {
  setOptOut(true);
};

export const amplitudeSendEvent = (
  event: `${EventAction}`,
  data?: EventCustomAttributes,
  callback?: () => void
) => {
  track(event, addGlobalEventData(data)).promise.then(callback);
};

// Amplitude doesn't have a first-class way of tracking activity durations, so we need to send a pair of events
// Should be utilized with amplitudeSendEndEvent()
export const amplitudeSendStartEvent = (
  event: `${EventTimedAction}`,
  data?: EventCustomAttributes,
  callback?: () => void
) => {
  amplitudeSendEvent(`${event}: start` as EventAction, data, callback);
};

// Amplitude doesn't have a first-class way of tracking activity durations, so we need to send a pair of events
// Should be utilized with amplitudeSendStartEvent()
export const amplitudeSendEndEvent = (
  event: `${EventTimedAction}`,
  data?: EventCustomAttributes,
  callback?: () => void
) => {
  amplitudeSendEvent(`${event}: end` as EventAction, data, callback);
};

// A conveinice wrapper to disambiguate flow end events due to abort (closing modal, etc)
export const amplitudeSendAbortEvent = (
  event: `${EventTimedAction}`,
  data?: EventCustomAttributes,
  callback?: () => void
) => {
  amplitudeSendEvent(`${event}: abort` as EventAction, data, callback);
};

const addGlobalEventData = (data?: EventCustomAttributes) => {
  if (!window || !window.location) {
    return data;
  }

  return {
    ...data,
    pathname: window.location.pathname,
  };
};

export const amplitudeSetUserId = (uuid: string) => {
  setUserId(uuid);
};

/** Sets a user property for the current-user. */
export const amplitudeSetUserProperty = (property: UserProperty) => {
  const identify = new Identify();
  identify.set(property.property, property.value);
  amplitudeIdentify(identify);
};

/** Sets a user property one time for the current user. This setting persists across refreshes. */
export const amplitudeSetUserPropertyOnce = (property: UserProperty) => {
  const identify = new Identify();
  identify.setOnce(property.property, property.value);
  amplitudeIdentify(identify);
};

const getAppType = (): UserPropertyDeviceApp['value'] => {
  if (window.RUNNING_IN_WEBVIEW) {
    return 'Tock-Dashboard App (iOS)';
  }
  return 'Web';
};

export const getDeviceId = () => {
  // For some reason lately, evaluation of amplitudeGetDeviceId() in development builds is
  // surprisingly returing undefined. This can mask failure, in development, to append a
  // ?deviceId= query param to a URL that ought to have one. So let's supply something.
  return (
    amplitudeGetDeviceId() ??
    (import.meta.env.DEV
      ? 'heyThisIsNotARealDeviceIdYouAreSeeingThisBecauseThisIsADevBuild'
      : undefined)
  );
};

export const ticketTypeToAnalyticsData = (ticketType?: core.ITicketType): EventCustomAttributes => {
  if (!ticketType) {
    return {};
  }
  return {
    ticketTypeId: ticketType.id || undefined,
    ticketTypeVariety: ticketType.variety || undefined,
    ticketTypeName: ticketType.name || undefined,
  };
};

export const purchaseOrderToAnalyticsData = (
  purchaseOrder?: core.IPurchaseOrder
): EventCustomAttributes => {
  if (!purchaseOrder) {
    return {};
  }

  return {
    ticketTypeId: purchaseOrder.ticketTypeId || undefined,
    ticketTypeName: purchaseOrder.ticketTypeName || undefined,
    purchaseId: purchaseOrder.id || undefined,
    originalPurchaseId: purchaseOrder.basePurchaseId || undefined,
    seatingAreaName: purchaseOrder.seatingAreaName || undefined,
  };
};

export const purchaseToAnalyticsData = (purchase?: core.IPastPurchase): EventCustomAttributes => {
  if (!purchase) {
    return {};
  }
  return {
    ticketTypeId: purchase.ticketTypeId || undefined,
    ticketTypeName: purchase.ticketTypeName || undefined,
    purchaseId: purchase.id || undefined,
    originalPurchaseId: purchase.originalPurchaseId || undefined,
    seatingAreaName: purchase.seatingAreaName || undefined,
  };
};
